import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ManufacturedProductsViewModel } from "../../viewmodels/production/ManufacturedProductsViewModel";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";

import { formatDateWithTime, withRouter } from "../../shared/utility";

import SlideOver from "../../components/slideOver/SlideOver";

import EmptyModal from "../../components/modals/EmptyModal";
import ActionButton from "../../components/controls/ActionButton";
import ActionLink from "../../components/controls/ActionLink";
import Button from "../../components/ui/Button";

import { Paginator } from "primereact/paginator";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import ReportsDropdown from "../../components/filterDropdown/ReportsDropdown";
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
import OperationalAnalyticsStrip from "../../components/operational/OperationalAnalyticsStrip";
import OperationalInlineFilters from "../../components/operational/OperationalInlineFilters";
import OperationalTableToolbar from "../../components/operational/OperationalTableToolbar";
import OperationalDataTableSkeleton from "../../components/operational/OperationalDataTableSkeleton";
import Pills from "../../components/controls/Pills";
import { productionStatusOptions } from "../../shared/utility";
import {
  excludeAllOption,
  formatDateRangeSummary,
  isMultiFilterActive,
} from "../../shared/filterUtils";
import AuditActionLink from "../../components/audit/AuditActionLink";
import AuditHistoriesLink from "../../components/audit/AuditHistoriesLink";

const SEARCH_DEBOUNCE_MS = 400;

function ManufacturedProducts(props) {
  const { addSnackbar, logout } = props;
  const {
    emptyModal,
    fetchRecipesForDropdown,
    getCertifiedByOptionsForDropdown,
    getManufacturedProducts,
    loading,
    productionHistories,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    askDeleteManufacturedProduct,
    filters,
    resetFilters,
    showFilters,
    setShowFilters,
    getUsersForDropdown,
    certifications,
    recipes,
    supervisorOptions,
    employeeOptions,
    statusFilterOptions,
    generateProductionReport,
    reportComponents,
    showReports,
    setShowReports,
    reportInProgress,
    analytics,
    setFilters,
    setShowDates,
    showDates,
    fetchProductsForDropdown,
    productsForDropdown,
  } = ManufacturedProductsViewModel(logout, addSnackbar);

  const productOptions = excludeAllOption(productsForDropdown);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const searchRef = useRef();
  const searchDebounceRef = useRef(null);

  const getSearchValue = () => searchRef.current?.value?.trim() || null;

  useEffect(() => {
    fetchRecipesForDropdown();
    getUsersForDropdown(2, [1, 3]);
    getUsersForDropdown(1, [1, 3]);
    getCertifiedByOptionsForDropdown();
    fetchProductsForDropdown();
    getManufacturedProducts(null, 1, filters);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleSearch = (filterOverride = filters) => {
    setFirst(0);
    getManufacturedProducts(getSearchValue(), 1, filterOverride);
  };

  const scheduleSearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      handleSearch();
    }, SEARCH_DEBOUNCE_MS);
  };

  const handlePagination = (event) => {
    const currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getManufacturedProducts(getSearchValue(), currentPage, filters);
  };

  const applyFilterChange = (nextFilters) => {
    setFirst(0);
    setFilters(nextFilters);
    getManufacturedProducts(getSearchValue(), 1, nextFilters);
  };

  const applyDateFilter = (startDate, endDate) => {
    const nextFilters = {
      ...filters,
      startDate: startDate !== undefined ? startDate : filters.startDate,
      endDate: endDate !== undefined ? endDate : filters.endDate,
    };
    applyFilterChange(nextFilters);
  };

  const clearDates = () => {
    applyFilterChange({
      ...filters,
      startDate: null,
      endDate: null,
    });
  };

  const handleClearAll = () => {
    if (searchRef.current) {
      searchRef.current.value = "";
    }
    setShowFilters(false);
    resetFilters();
    setFirst(0);
  };

  const convertProductStatus = (status) => {
    const statusOption = productionStatusOptions.filter(
      (el) => el.value === status,
    );

    return statusOption.length === 1
      ? { text: statusOption[0].label, type: statusOption[0].type }
      : {
          text: productionStatusOptions[0].label,
          type: productionStatusOptions[0].type,
        };
  };

  const totalRecords = productionHistories?.total ?? 0;
  const hasDateFilter = Boolean(filters.startDate && filters.endDate);
  const rangeSummary = formatDateRangeSummary(
    filters.startDate,
    filters.endDate,
  );

  const activeFilterCount = [
    isMultiFilterActive(filters.product),
    isMultiFilterActive(filters.status),
    filters.lot_number,
    isMultiFilterActive(filters.certification),
    isMultiFilterActive(filters.recipe),
    isMultiFilterActive(filters.supervisor),
    isMultiFilterActive(filters.employee1),
    isMultiFilterActive(filters.employee2),
  ].filter(Boolean).length;

  const hasAnyFilters = hasDateFilter || activeFilterCount > 0;

  const resultMeta = `${totalRecords.toLocaleString()} result${totalRecords === 1 ? "" : "s"}`;

  const statusBody = (rowData) =>
    rowData.current_in_process_status_id &&
    rowData.current_in_process_status_name ? (
      <span className="inline-flex items-center rounded-md bg-orange-500 px-2 py-1 text-sm font-medium text-white">
        {rowData.current_in_process_status_name}
      </span>
    ) : (
      <Pills content={convertProductStatus(rowData.status)} />
    );

  const actionsBody = (rowData) => (
    <div className="flex flex-wrap items-center gap-2">
      <ActionLink
        to={"/manufactured-products/" + rowData.id}
        variant="primary"
        target="_blank"
        rel="noopener noreferrer"
      >
        View details
      </ActionLink>
      <AuditActionLink
        slug="manufactured-product"
        id={rowData.id}
        name={
          rowData.lot_number ??
          rowData.product_name ??
          rowData.plu ??
          `#${rowData.id}`
        }
      />
      <ActionButton
        color="red"
        onClick={() => {
          askDeleteManufacturedProduct(rowData);
        }}
      >
        Delete
      </ActionButton>
    </div>
  );

  const emptyMessage = (
    <div className="px-6 py-12 text-center">
      <p className="text-sm text-[#64748B]">
        No manufactured products match the current filters.
      </p>
      {hasAnyFilters ? (
        <Button
          type="button"
          variant="soft"
          size="md"
          className="mt-4"
          onClick={handleClearAll}
        >
          Clear filters
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className="qatrace-screen qatrace-module-page">
      <div>
        <div className="space-y-5 px-4 sm:px-6 lg:px-8">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
                <li>
                  <Link
                    to="/manufacture-product"
                    className="text-[#16A34A] transition hover:text-[#15803D]"
                  >
                    Production
                  </Link>
                </li>
                <li className="text-[#CBD5E1]" aria-hidden="true">
                  /
                </li>
                <li className="text-[#475569]">Manufactured Products</li>
              </ol>
            </nav>

            <div className="flex flex-wrap items-center gap-2">
              <AuditHistoriesLink slug="manufactured-product" />
              <ReportsDropdown
                actionCall={generateProductionReport}
                reportComponents={reportComponents}
                showReports={showReports}
                setShowReports={setShowReports}
                title="Reports"
                reportInProgress={reportInProgress}
                triggerVariant="secondary"
              />
              <Link
                to="/manufacture-product"
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-[#16A34A] bg-[#16A34A] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:border-[#15803D] hover:bg-[#15803D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#16A34A]"
              >
                <span className="text-lg leading-none">+</span>
                New Record
              </Link>
            </div>
          </header>

          <OperationalAnalyticsStrip
            stats={analytics?.stats}
            loading={analytics?.loading}
          />

          <div className="overflow-visible rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <OperationalTableToolbar
              searchRow={
                <>
                  <MagnifyingGlassIcon
                    className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#94A3B8]"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    name="searchProduct"
                    id="searchProduct"
                    className="block min-h-10 w-full rounded-xl border border-[#E2E8F0] bg-white py-2 pl-10 pr-3 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
                    placeholder="Search product, PLU, lot number..."
                    ref={searchRef}
                    onChange={scheduleSearch}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (searchDebounceRef.current) {
                          clearTimeout(searchDebounceRef.current);
                        }
                        handleSearch();
                      }
                    }}
                  />
                </>
              }
              filterActions={
                <>
                  <DateRangeFilter
                    onChange={(startDate, endDate) =>
                      setFilters({
                        ...filters,
                        startDate: startDate,
                        endDate: endDate,
                      })
                    }
                    onClear={clearDates}
                    endDate={filters.endDate}
                    startDate={filters.startDate}
                    setShowDates={setShowDates}
                    showDates={showDates}
                    applyChanges={applyDateFilter}
                    maxDate={new Date()}
                    rangeSummary={hasDateFilter ? rangeSummary : undefined}
                  />
                  <Button
                    type="button"
                    variant={showFilters ? "soft" : "secondary"}
                    size="md"
                    onClick={() => setShowFilters(!showFilters)}
                    aria-expanded={showFilters}
                  >
                    <AdjustmentsHorizontalIcon
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                    {showFilters ? "Hide filters" : "Filters"}
                    {activeFilterCount > 0 ? (
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#16A34A] px-1.5 text-xs font-bold leading-none text-white">
                        {activeFilterCount}
                      </span>
                    ) : null}
                  </Button>
                </>
              }
              footer={
                showFilters ? (
                  <OperationalInlineFilters
                    filters={filters}
                    onFilterChange={applyFilterChange}
                    onLotDraftChange={(lotNumber) =>
                      setFilters({ ...filters, lot_number: lotNumber })
                    }
                    productOptions={productOptions}
                    statusOptions={excludeAllOption(statusFilterOptions)}
                    certificationOptions={excludeAllOption(certifications)}
                    recipeOptions={excludeAllOption(recipes)}
                    supervisorOptions={excludeAllOption(supervisorOptions)}
                    employeeOptions={excludeAllOption(employeeOptions)}
                  />
                ) : null
              }
              trailing={
                <>
                  <span className="text-xs font-medium text-[#64748B]">
                    {resultMeta}
                  </span>
                  {hasAnyFilters ? (
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="text-xs font-semibold text-[#16A34A] transition hover:text-[#15803D]"
                    >
                      Clear all
                    </button>
                  ) : null}
                </>
              }
            />

            <div className="operational-embedded-table overflow-hidden">
              {loading ? (
                <OperationalDataTableSkeleton
                  columns={[
                    "PLU",
                    "Product Name",
                    "Lot Number",
                    "Target Weight",
                    "Production Date",
                    "Status",
                    "Actions",
                  ]}
                  rows={10}
                />
              ) : (
                <DataTable
                  value={productionHistories.data ?? []}
                  dataKey="id"
                  className="p-datatable-sm text-sm"
                  emptyMessage={emptyMessage}
                >
                  <Column field="plu" header="PLU" style={{ minWidth: "6rem" }} />
                  <Column
                    field="product_name"
                    header="Product Name"
                    style={{ minWidth: "12rem" }}
                    bodyClassName="font-semibold text-[#0F172A]"
                  />
                  <Column
                    field="lot_number"
                    header="Lot Number"
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    header="Target Weight"
                    body={(row) => `${row.new_weight} kg`}
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    header="Production Date"
                    body={(row) => formatDateWithTime(row.production_date)}
                    style={{ minWidth: "10rem" }}
                  />
                  <Column
                    header="Status"
                    body={statusBody}
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    header="Actions"
                    body={actionsBody}
                    style={{ minWidth: "11rem" }}
                />
                </DataTable>
              )}
            </div>

            <div className="border-t border-[#E2E8F0] px-4 py-4 sm:px-5">
              <Paginator
                className="operational-embedded-paginator"
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPageChange={handlePagination}
              />
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ManufacturedProducts),
);
